import { z } from 'zod';
import type {
  BonusAccount,
  ConnectorReservation,
  MenuSnapshot,
  PaymentType,
  ReservationDraft,
  StopListEntry,
} from './menu';
import type { Order, OrderDraft } from './order';
import { IdSchema } from './common';

/**
 * Возможности коннектора. Ядро и виджеты проверяют ИМЕННО их, а не `kind`:
 * у одного вендора на разных объектах набор возможностей отличается.
 */
export const CAPABILITIES = [
  'menu.read',
  'stoplist.read',
  'order.create',
  'order.status',
  'order.cancel',
  'payment.online',
  'reservation',
  'loyalty',
] as const;
export const CapabilitySchema = z.enum(CAPABILITIES);
export type Capability = z.infer<typeof CapabilitySchema>;

export const CapabilitiesSchema = z.partialRecord(CapabilitySchema, z.boolean());
export type Capabilities = z.infer<typeof CapabilitiesSchema>;

export function hasCapability(caps: Capabilities, capability: Capability): boolean {
  return caps[capability] === true;
}

export function missingCapabilities(
  caps: Capabilities,
  required: readonly Capability[],
): Capability[] {
  return required.filter((capability) => !hasCapability(caps, capability));
}

export const ConnectorKindSchema = z.enum(['iiko', 'rkeeper', 'mock']);
export type ConnectorKind = z.infer<typeof ConnectorKindSchema>;

export const ConnectorHealthSchema = z.object({
  kind: ConnectorKindSchema,
  ok: z.boolean(),
  latencyMs: z.number().int().nonnegative(),
  checkedAt: z.string(),
  message: z.string().optional(),
});
export type ConnectorHealth = z.infer<typeof ConnectorHealthSchema>;

export const ConnectorOrderSchema = z.object({
  id: IdSchema,
  externalId: IdSchema,
  status: z.string(),
  number: z.string().optional(),
  createdAt: z.string(),
});
export type ConnectorOrder = z.infer<typeof ConnectorOrderSchema>;

export const ConnectorErrorCodeSchema = z.enum([
  'connector_unavailable',
  'connector_timeout',
  'connector_rejected',
  'unsupported_capability',
  'invalid_draft',
  'item_unavailable',
  'duplicate_order',
]);
export type ConnectorErrorCode = z.infer<typeof ConnectorErrorCodeSchema>;

export class ConnectorError extends Error {
  readonly code: ConnectorErrorCode;
  readonly retryable: boolean;

  constructor(code: ConnectorErrorCode, message: string, retryable = false) {
    super(message);
    this.name = 'ConnectorError';
    this.code = code;
    this.retryable = retryable;
  }
}

/**
 * Контракт адаптера учётной системы (эталон для Python-реализаций в services/connectors).
 * Обязательны только чтение меню и стоп-листов; остальное — по возможностям.
 */
export interface Connector {
  readonly kind: ConnectorKind;
  readonly capabilities: Capabilities;

  getMenu(sinceRevision?: string): Promise<MenuSnapshot>;
  getStopList(): Promise<StopListEntry[]>;
  getPaymentTypes(): Promise<PaymentType[]>;
  health(): Promise<ConnectorHealth>;

  createOrder?(draft: OrderDraft): Promise<ConnectorOrder>;
  getOrder?(externalId: string): Promise<Order | null>;
  cancelOrder?(externalId: string, reason: string): Promise<void>;
  createReservation?(draft: ReservationDraft): Promise<ConnectorReservation>;
  getGuestBonus?(phone: string): Promise<BonusAccount | null>;
  applyBonus?(phone: string, points: number, orderId: string): Promise<void>;
}
